import { useServiceWorker } from '@/hooks/useServiceWorker';
import { useAutoUpdate } from '@/hooks/useAutoUpdate';
import { useLocation } from 'react-router-dom';
import FaIcon from '@/components/common/FaIcon';
import { ArrowPathIcon, XMarkIcon } from '../icons';

function WifiOffIcon({ className }: { className?: string }) {
  return (
    <FaIcon icon="wifi" className={className} />
  );
}

export default function ServiceWorkerUpdateBanner() {
  const { isUpdateAvailable, isOffline, applyUpdate, dismissUpdate } = useServiceWorker();
  const location = useLocation();

  // Auto-apply a pending update on tabs that are safe to reload (hidden, or idle
  // >= 30 min, and not mid-task) so long-lived/display tabs don't drift onto a
  // stale frontend. Actively-used tabs still get the manual banner below.
  useAutoUpdate(isUpdateAvailable, applyUpdate);

  // Field ops mode has its own SyncStatusIndicator pill — skip the big banner
  const isFieldMode = location.pathname.startsWith('/field');

  // Public portal should never show internal app banners to citizens
  const isPublicPortal = location.pathname.startsWith('/public/');
  if (isPublicPortal) return null;

  // Show offline banner (not in field ops mode — it has its own indicator)
  if (isOffline && !isFieldMode) {
    return (
      <div className="relative z-50 bg-yellow-500 text-yellow-900 dark:text-yellow-400 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm font-medium">
          <WifiOffIcon className="h-5 w-5" />
          <span>You&apos;re offline. Some features may be limited.</span>
        </div>
      </div>
    );
  }

  // Show update available banner — a fixed overlay (never in document flow, so
  // it can't push the app shell down or create a transient scrollbar).
  //
  // F-012: as a full-width bar this swallowed every click in the top strip of
  // the viewport. The shelter detail panel's action bar sits at y≈16–38, so
  // while the banner showed, Delete and Close were unclickable — the clicks
  // landed on the banner, with nothing to indicate why. Any top-anchored
  // control in the app had the same problem.
  //
  // Now a centred pill inside a click-through wrapper: the wrapper is
  // pointer-events-none so anything it overlaps stays reachable, and only the
  // pill itself takes clicks.
  if (isUpdateAvailable) {
    return (
      <div className="fixed top-0 inset-x-0 z-[60] pointer-events-none flex justify-center px-4 pt-2">
        <div className="pointer-events-auto animate-banner-in bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg max-w-3xl w-full flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <ArrowPathIcon className="h-5 w-5" />
            <span>A new version of Command Bridge is available!</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={applyUpdate}
              className="px-3 py-1 bg-white dark:bg-primary-700 text-primary-600 dark:text-white rounded-md text-sm font-medium hover:bg-primary-50 dark:hover:bg-primary-800 transition-colors"
            >
              Update Now
            </button>
            <button
              onClick={dismissUpdate}
              className="p-1 hover:bg-primary-700 rounded transition-colors"
              aria-label="Dismiss"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
